import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, FlatList, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ThemeName, useTheme, useThemedStyles, Theme } from '../theme';

const COLOR_OPTIONS = [
  { name: 'デフォルト', color: '#00B900' },
  { name: 'ブルー', color: '#007AFF' },
  { name: 'レッド', color: '#FF3B30' },
  { name: 'パープル', color: '#AF52DE' },
  { name: 'オレンジ', color: '#FF9500' },
  { name: 'ピンク', color: '#FF6B9D' },
  { name: 'ティール', color: '#5AC8FA' },
  { name: 'イエロー', color: '#FFCC00' },
];

interface Props {
  onBack: () => void;
}

const OPTIONS: { key: ThemeName; title: string; description: string }[] = [
  { key: 'system', title: '規定の明るさ', description: 'OSの設定に追従します' },
  { key: 'light', title: 'ライト', description: '明るいテーマを使用します' },
  { key: 'dark', title: 'ダーク', description: '暗いテーマを使用します' },
];

const ThemeSettings: React.FC<Props> = ({ onBack }) => {
  const { themeName, setThemeName, theme, setPrimaryColor } = useTheme();
  const insets = useSafeAreaInsets();
  const styles = useThemedStyles(createStyles);
  const [selectedColor, setSelectedColor] = useState(theme.colors.primary);

  React.useEffect(() => {
    setSelectedColor(theme.colors.primary);
  }, [theme.colors.primary]);

  const handleColorSelect = (color: string) => {
    setSelectedColor(color);
    setPrimaryColor(color);
  };

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}> 
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.headerIcon}>
          <Ionicons name="chevron-back" size={24} color={theme.colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>テーマ</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>テーマカラー</Text>
          <View style={styles.colorGrid}>
            {COLOR_OPTIONS.map((option) => (
              <TouchableOpacity
                key={option.color}
                style={styles.colorOption}
                onPress={() => handleColorSelect(option.color)}
              >
                <View 
                  style={[
                    styles.colorSwatch, 
                    { backgroundColor: option.color },
                    selectedColor === option.color && styles.colorSwatchSelected
                  ]} 
                >
                  {selectedColor === option.color && (
                    <Ionicons name="checkmark" size={20} color="#FFF" />
                  )}
                </View>
                <Text style={styles.colorName}>{option.name}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>明るさ</Text>
          <FlatList
            scrollEnabled={false}
        data={OPTIONS}
        keyExtractor={(item) => item.key}
        renderItem={({ item }) => {
          const selected = item.key === themeName;
          return (
            <TouchableOpacity
              onPress={() => setThemeName(item.key)}
              style={styles.item}
              activeOpacity={0.7}
            >
              <View style={styles.itemTextContainer}>
                <Text style={styles.itemTitle}>{item.title}</Text>
                <Text style={styles.itemDescription}>{item.description}</Text>
              </View>
              <View style={[styles.radio, selected && styles.radioSelected]}>
                {selected && <Ionicons name="checkmark" size={16} color={theme.colors.primaryText} />}
              </View>
            </TouchableOpacity>
          );
        }}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
            contentContainerStyle={styles.listContent}
          />
        </View>
      </ScrollView>
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  headerIcon: {
    padding: 8,
  },
  headerSpacer: {
    width: 24,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  listContent: {
    backgroundColor: theme.colors.card,
    marginTop: 16,
    borderTopWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: theme.colors.border,
  },
  item: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  itemTextContainer: {
    flex: 1,
  },
  itemTitle: {
    fontSize: 16,
    color: theme.colors.text,
    fontWeight: '600',
  },
  itemDescription: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginTop: 4,
  },
  radio: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.card,
  },
  radioSelected: {
    borderColor: theme.colors.primary,
    backgroundColor: theme.colors.primary,
  },
  separator: {
    height: 0.5,
    backgroundColor: theme.colors.border,
    marginLeft: 16,
  },
  section: {
    marginTop: 16,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.colors.textMuted,
    paddingHorizontal: 16,
    paddingVertical: 8,
    textTransform: 'uppercase',
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 8,
    backgroundColor: theme.colors.card,
    paddingVertical: 16,
  },
  colorOption: {
    width: '25%',
    alignItems: 'center',
    marginBottom: 16,
  },
  colorSwatch: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginBottom: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: theme.colors.border,
  },
  colorSwatchSelected: {
    borderColor: theme.colors.text,
    borderWidth: 3,
  },
  colorName: {
    fontSize: 11,
    color: theme.colors.textMuted,
    textAlign: 'center',
  },
});

export default ThemeSettings;
